import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { partnerAPI } from '../services/api';

// The tenants assigned to this partner, set like the rest of the suite: a page head, three figures
// on a hairline strip, then the tenants as typeset rows. Status keeps a distinct hue — active cyan,
// anything else brass — because "which of these is still live" is the question this page answers.
export default function DashboardPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnerAPI.getTenants();
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = useMemo(() => {
    if (!search.trim()) return tenants;
    const q = search.toLowerCase();
    return tenants.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.subdomain?.toLowerCase().includes(q)
    );
  }, [tenants, search]);

  const activeTenants = tenants.filter((t) => t.status === 'active').length;

  const handleTenantClick = (tenantId) => {
    navigate(`/access/${tenantId}`);
  };

  return (
    <Box className="pt-page">
      <Box className="pt-head">
        <Box>
          <p className="pt-eyebrow">Partners</p>
          <h1 className="pt-head__title">Your tenants</h1>
          <p className="pt-head__lede">Pick a tenant to open their platform.</p>
        </Box>
        {tenants.length > 3 && (
          <TextField
            placeholder="Search tenants..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {!loading && tenants.length > 0 && (
        <ul className="pt-stats">
          <li className="pt-stat">
            <span className="pt-stat__label">Tenants</span>
            <span className="pt-stat__value">{tenants.length}</span>
          </li>
          <li className="pt-stat">
            <span className="pt-stat__label">Active</span>
            <span className="pt-stat__value" style={{ color: 'var(--pt-cyan)' }}>{activeTenants}</span>
          </li>
          <li className="pt-stat">
            <span className="pt-stat__label">Expiring / inactive</span>
            <span className="pt-stat__value" style={{ color: tenants.length - activeTenants ? 'var(--pt-brass)' : undefined }}>
              {tenants.length - activeTenants}
            </span>
          </li>
        </ul>
      )}

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

      <Box className="pt-section">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredTenants.length === 0 && search ? (
          <Box className="pt-empty">
            <p className="pt-empty__title">No tenants matching &ldquo;{search}&rdquo;</p>
          </Box>
        ) : filteredTenants.length === 0 ? (
          <Box className="pt-empty">
            <p className="pt-empty__title">No tenants assigned</p>
            <p>Contact your administrator to get access to tenant accounts.</p>
          </Box>
        ) : (
          <ul className="pt-rows">
            {filteredTenants.map((tenant) => (
              <li key={tenant.tenant_id}>
                <Box
                  component="a"
                  href={`/access/${tenant.tenant_id}`}
                  className="pt-row"
                  onClick={(e) => { e.preventDefault(); handleTenantClick(tenant.tenant_id); }}
                >
                  <span className="pt-row__main">
                    <span className="pt-row__title">{tenant.name}</span>
                    <span className="pt-tag pt-tag--quiet pt-tag--bare">{tenant.subdomain}.elebyte.io</span>
                  </span>
                  <span className={`pt-tag ${tenant.status === 'active' ? '' : 'pt-tag--brass'}`}>
                    {tenant.status}
                  </span>
                  <span className="pt-row__meta">
                    {tenant.expires_at ? `Expires ${new Date(tenant.expires_at).toLocaleDateString()}` : ''}
                  </span>
                </Box>
              </li>
            ))}
          </ul>
        )}
      </Box>
    </Box>
  );
}
